/**
 * EisenhowerMatrix — grille 2x2 des 4 quadrants avec drag-and-drop.
 *
 * Utilise dnd-kit/core pour détecter un drop sur un quadrant et
 * appelle moveTask() du taskStore. L'overlay affiche un aperçu
 * fantôme de la carte en cours de glisser.
 */
import { useState } from 'react';
import type { JSX } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useTaskStore } from '@store/taskStore';
import type { Quadrant, Task } from '@store/types';
import { QuadrantColumn, QUADRANTS } from './QuadrantColumn';
import { TaskCard } from './TaskCard';

export interface EisenhowerMatrixProps {
  onAddTask?: (quadrant: Quadrant) => void;
  onStartTask?: (task: Task) => void;
}

export function EisenhowerMatrix({
  onAddTask,
  onStartTask,
}: EisenhowerMatrixProps): JSX.Element {
  const tasks = useTaskStore((s) => s.tasks);
  const moveTask = useTaskStore((s) => s.moveTask);
  const completeTask = useTaskStore((s) => s.completeTask);
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const byQuadrant: Record<Quadrant, Task[]> = { Q1: [], Q2: [], Q3: [], Q4: [] };
  for (const t of tasks) {
    if (t.status !== 'done') byQuadrant[t.quadrant].push(t);
  }
  (Object.keys(byQuadrant) as Quadrant[]).forEach((q) => {
    byQuadrant[q].sort((a, b) => b.aiScore - a.aiScore);
  });

  const handleDragStart = (event: DragStartEvent) => {
    const task = tasks.find((t) => t.id === event.active.id);
    setActiveTask(task ?? null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveTask(null);
    const { active, over } = event;
    if (!over) return;

    const taskId = String(active.id);
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    // "over" peut être soit un quadrant (id Q1-Q4), soit une autre tâche.
    const overId = String(over.id);
    const targetQuadrant: Quadrant | undefined =
      (['Q1', 'Q2', 'Q3', 'Q4'] as Quadrant[]).find((q) => q === overId) ??
      (over.data.current?.quadrant as Quadrant | undefined);

    if (targetQuadrant && targetQuadrant !== task.quadrant) {
      moveTask(taskId, targetQuadrant);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {QUADRANTS.map((meta) => (
          <QuadrantColumn
            key={meta.id}
            meta={meta}
            tasks={byQuadrant[meta.id]}
            onAdd={onAddTask}
            onStartTask={onStartTask}
            onCompleteTask={(task) => completeTask(task.id)}
          />
        ))}
      </div>

      <DragOverlay>
        {activeTask ? (
          <div className="opacity-95 rotate-1">
            <TaskCard task={activeTask} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
